import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { VpnKey as VpnKeyIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  fetchAppAccessKeySettings,
  updateAppAccessKeySettings,
} from '../../services/accessKeyService';
import { revokeAccessSession } from '../../utils/accessKey';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';

type Props = {
  isSuperuser: boolean;
};

const AppAccessKeySettingsCard: React.FC<Props> = ({ isSuperuser }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [currentKey, setCurrentKey] = useState('');
  const [newKey, setNewKey] = useState('');
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!isSuperuser) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchAppAccessKeySettings();
      setEnabled(Boolean(data.enabled));
      setCurrentKey(data.access_key || '');
      setNewKey(data.access_key || '');
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '접근 키 정보를 불러오기 실패'));
    } finally {
      setLoading(false);
    }
  }, [isSuperuser]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setError('');
    if (!newKey.trim()) {
      setError('접근 키를 입력');
      return;
    }
    if (!adminUsername.trim() || !adminPassword) {
      setError('최고관리자 아이디와 비밀번호를 입력');
      return;
    }
    setSaving(true);
    try {
      const res = await updateAppAccessKeySettings({
        username: adminUsername.trim(),
        password: adminPassword,
        access_key: newKey.trim(),
      });
      if (res?.success === false) {
        setError(res?.message || '접근 키 저장에 실패');
        return;
      }
      enqueueSnackbar(res?.message || '접근 키가 저장 완료', {
        variant: 'success',
      });
      setAdminPassword('');
      revokeAccessSession();
      await load();
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '접근 키 저장에 실패'));
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperuser) {
    return (
      <Card>
        <CardHeader title="앱 접속 URL 키" />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            접근 키 변경은 최고관리자만 가능
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="앱 접속 URL 키"
        subheader="브라우저 주소에 ?key=값 으로 최초 접속 (저장 후 주소창에서는 키가 숨겨짐)"
        action={
          <Button size="small" onClick={load} disabled={loading || saving}>
            새로고침
          </Button>
        }
      />
      <CardContent>
        {loading ? (
          <CircularProgress size={28} />
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 2 }}>
              현재 게이트: {enabled ? '사용 중' : '비활성(키 없음)'}
              {currentKey ? ` · 설정된 키 길이 ${currentKey.length}자` : ''}
            </Alert>
            <TextField
              fullWidth
              label="접근 키 (새 값)"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              sx={{ mb: 2 }}
              helperText="예: godislove — 변경 후에는 새 키가 포함된 URL로 다시 접속해야."
            />
            <TextField
              fullWidth
              label="최고관리자 아이디"
              value={adminUsername}
              onChange={(e) => setAdminUsername(e.target.value)}
              autoComplete="username"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="최고관리자 비밀번호"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              autoComplete="current-password"
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              startIcon={<VpnKeyIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '저장 중…' : '접근 키 저장'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AppAccessKeySettingsCard;
